import React from 'react';
import { Box, Dialog, Menu, MenuItem, ListItemIcon, ListItemText, useMediaQuery, useTheme } from '@mui/material';
import Txt from './Txt';
import { bottomSheetPaperSx, bottomSheetDialogSx } from './sheetPaperSx';

const mono = 'var(--font-mono)';

export interface CardAction {
  key: string;
  label: string;
  /** One short line under the label (phone sheet only). */
  hint?: string;
  Icon: React.ElementType;
  danger?: boolean;
  run: () => void;
}

interface CardActionMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** Shown as the sheet's caption on phones — the card's name. */
  title?: string;
  actions: CardAction[];
}

/**
 * The ⋮ menu behind every card's secondary actions (exercise cards, My Foods).
 * Phones get a bottom sheet: a popover anchored to a card is capped at the space
 * left below it, which clipped the last item (Delete) off the end and made it
 * unreachable. Desktop gets an ordinary anchored menu.
 */
const CardActionMenu: React.FC<CardActionMenuProps> = ({ anchorEl, onClose, title, actions }) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const open = !!anchorEl;
  const runAndClose = (fn: () => void) => () => { onClose(); fn(); };

  if (phone) {
    return (
      <Dialog open={open} onClose={onClose} sx={bottomSheetDialogSx(true)} PaperProps={{ sx: { ...bottomSheetPaperSx(true, 320), p: 0 } }}>
        <Box sx={{ pt: 1.25, pb: 'calc(8px + env(safe-area-inset-bottom, 0px))' }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.2)', mx: 'auto', mb: 1 }} />
          {title && (
            <Txt sx={{ px: 2, pb: 1, fontFamily: mono, fontSize: '0.7rem', letterSpacing: 1.2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {title}
            </Txt>
          )}
          {actions.map((a) => (
            <Box
              key={a.key}
              onClick={runAndClose(a.run)}
              className="icon-text-row"
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 2, minHeight: 56, cursor: 'pointer',
                borderTop: '1px solid rgba(255,255,255,0.06)',
                color: a.danger ? '#FF1744' : '#fff',
                '&:active': { bgcolor: 'rgba(255,255,255,0.06)' },
              }}
            >
              <a.Icon sx={{ fontSize: 20, color: a.danger ? '#FF1744' : '#00E0FF', flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Txt sx={{ fontSize: '0.9rem', fontWeight: 600, lineHeight: 1.3 }}>{a.label}</Txt>
                {a.hint && <Txt sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.3 }}>{a.hint}</Txt>}
              </Box>
            </Box>
          ))}
        </Box>
      </Dialog>
    );
  }

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{ sx: { border: '1px solid rgba(0,224,255,0.2)', minWidth: 230, '& .MuiMenuItem-root': { minHeight: 44, fontSize: '0.9rem' }, '& .MuiListItemIcon-root': { minWidth: 34, color: '#00E0FF' } } }}
    >
      {actions.map((a) => (
        <MenuItem key={a.key} onClick={runAndClose(a.run)} sx={a.danger ? { color: '#FF1744', '& .MuiListItemIcon-root': { color: '#FF1744 !important' } } : undefined}>
          <ListItemIcon><a.Icon fontSize="small" /></ListItemIcon>
          <ListItemText>{a.label}</ListItemText>
        </MenuItem>
      ))}
    </Menu>
  );
};

export default CardActionMenu;
